/**
 * Roteiro do tour de novidades (componente TourNovidades.vue).
 *
 * VERSAO: ao trocar, todos os usuários voltam a ver as boas-vindas e os tours.
 * destaques: cartões da janela "O que há de novo".
 * tours: passos guiados por tela. Um tour vale para a página quando
 *   `pagina` (regex do caminho) e `presente` (seletor no DOM) conferem.
 *   Passo: { alvo: seletor, titulo, texto, menu: true se o alvo fica no menu lateral }.
 *   Passos sem alvo visível na tela são pulados.
 */

export const VERSAO = '2026-10'

const UNIDADE = '^/Access/Unity/Unitie/\\d+'

export const destaques = [
    {
        icone: 'fa-solid fa-table-columns',
        titulo: 'Novo layout',
        texto: 'Menu lateral com os links da unidade, barra superior com o caminho da página e visual unificado em todas as telas.'
    },
    {
        icone: 'fa-solid fa-chart-line',
        titulo: 'Dashboard mais rápido',
        texto: 'Indicadores de WebHooks clicáveis que filtram a lista, atualização sem recarregar e gráficos anuais de processos e ofertas.'
    },
    {
        icone: 'fa-solid fa-table-list',
        titulo: 'Tabelas mais práticas',
        texto: 'Busca enquanto digita, filtro e ordenação em cada coluna, escolha de colunas e densidade em "Exibição", exportação para Excel e cartões no celular.'
    },
    {
        icone: 'fa-solid fa-clipboard-list',
        titulo: 'Oferta de processo seletivo',
        texto: 'Configuração guiada em passos, filtro de pendentes, nome da turma do ERP em cada card e detalhes com o retorno da exportação.'
    },
    {
        icone: 'fa-solid fa-globe',
        titulo: 'Oferta de inscrição web',
        texto: 'Exportação das turmas agrupadas por curso, com resumo, filtro de inscrições abertas e situação de cada turma.'
    },
    {
        icone: 'fa-solid fa-gear',
        titulo: 'Configurações da unidade',
        texto: 'Abas que avisam pendências, macros clicáveis para montar as máscaras e relação de documentos CRM → ERP mais clara.'
    },
    {
        icone: 'fa-solid fa-list-check',
        titulo: 'Gerenciador de CronJobs',
        texto: 'Tarefas filtradas por situação, frequência em português, formulário com busca de comandos e histórico de execuções por dia.',
        // só administradores do sistema veem o gerenciador
        visivel: () => !!document.querySelector('.ec-sidebar a[href$="/schedule"]')
    },
    {
        icone: 'fa-solid fa-spinner',
        titulo: 'Carregamento mais leve',
        texto: 'Novo indicador de carregamento, com botão Cancelar e transição suave entre as páginas.'
    }
]

export const tours = [
    {
        id: 'geral',
        titulo: 'Visão geral',
        passos: [
            {
                alvo: '.ec-sidebar',
                menu: true,
                titulo: 'Menu lateral',
                texto: 'Toda a navegação agora fica aqui, organizada por seções. Em telas menores ele vira uma gaveta, aberta pelo botão <i class="fa-solid fa-bars"></i> no topo.'
            },
            {
                alvo: '.ec-sidebar__unidade',
                menu: true,
                titulo: 'Unidade atual',
                texto: 'Dentro de uma unidade, os links dela aparecem logo abaixo. Se você tem acesso a mais de uma, troque de unidade direto por aqui.'
            },
            {
                alvo: '.ec-topo__titulo',
                titulo: 'Onde você está',
                texto: 'A barra superior mostra o título da página e um seletor para ir direto a outra unidade, de qualquer tela.'
            },
            {
                alvo: '.ec-topo__usuario',
                titulo: 'Sua conta',
                texto: 'Seus dados, informações do sistema e a opção de sair ficam neste menu.'
            },
            {
                alvo: '.ec-novidades-botao',
                titulo: 'Novidades sempre à mão',
                texto: 'Quer rever o tour? Clique aqui a qualquer momento. Quando uma tela tiver novidades que você ainda não viu, aparece uma bolinha.'
            }
        ]
    },
    {
        id: 'dashboard',
        titulo: 'Dashboard da unidade',
        pagina: new RegExp(UNIDADE + '/?$'),
        passos: [
            {
                alvo: '.ec-kpis',
                titulo: 'Indicadores clicáveis',
                texto: 'Cada cartão mostra o total de WebHooks em um status. Clique nele para filtrar a lista logo abaixo.'
            },
            {
                alvo: '.ec-webhooks__barra',
                titulo: 'Lista sempre atualizada',
                texto: 'Veja quantos registros estão na tela e use <b>Atualizar</b> para buscar os mais recentes sem recarregar a página.'
            },
            {
                alvo: '.ec-webhooks__tabela',
                titulo: 'WebHooks em detalhes',
                texto: 'Inscrito e turma na mesma coluna, status colorido e o sino com as notificações não lidas de cada WebHook.'
            },
            {
                alvo: 'details[id^="totalizadodes_inscricao_"]',
                titulo: 'Evolução anual',
                texto: 'Abra este card para ver os gráficos de processos e ofertas por ano, com atalho para as ofertas.'
            }
        ]
    },
    {
        id: 'listagens',
        titulo: 'Listagens',
        presente: '.ec-listagem',
        passos: [
            {
                alvo: '.ec-listagem .ec-busca',
                titulo: 'Busca enquanto digita',
                texto: 'A lista é filtrada conforme você digita. <kbd>Esc</kbd> limpa a busca.'
            },
            {
                alvo: '.ec-listagem .ec-tabela__ordenavel',
                titulo: 'Ordene pelas colunas',
                texto: 'Clique no título de uma coluna para ordenar; clique de novo para inverter a ordem.'
            },
            {
                alvo: '.ec-listagem .ec-por-pagina',
                titulo: 'Itens por página',
                texto: 'Escolha quantos registros exibir de cada vez. A paginação fica no fim da lista.'
            }
        ]
    },
    {
        id: 'oferta-ps-configurar',
        titulo: 'Configurar oferta de processo seletivo',
        pagina: new RegExp(UNIDADE + '/oferta_processo_seletivo/create'),
        passos: [
            {
                alvo: '.cps-passos',
                titulo: 'Passo a passo na tela',
                texto: 'O caminho completo agora fica descrito aqui: buscar, configurar a turma do ERP em cada curso e salvar.'
            },
            {
                alvo: '#anoInicio',
                titulo: 'Comece pelo ano letivo',
                texto: 'Informe o ano e clique em <b>Buscar processos</b>. A consulta ao ERP ficou bem mais rápida.'
            },
            {
                alvo: '.cps-barra',
                titulo: 'Resumo e filtros',
                texto: 'Veja quantos cursos já estão configurados e quantos faltam. Busque por curso ou turno e ligue <b>Só pendentes</b> para focar no que falta.'
            },
            {
                alvo: '.cps-pse',
                titulo: 'Um card por processo',
                texto: 'Abra o card para ver os cursos. Cada um mostra o nome da turma do ERP configurada e o botão <b>Configurar</b> / <b>Alterar</b>.'
            }
        ]
    },
    {
        id: 'oferta-ps-detalhe',
        titulo: 'Detalhes do processo seletivo',
        presente: '.csp',
        passos: [
            {
                alvo: '.csp > .ec-card',
                titulo: 'Tudo sobre o processo',
                texto: 'Período, tipo, chamada e situação das inscrições, importados do ERP, reunidos num só card.'
            },
            {
                alvo: '.csp-totais',
                titulo: 'Totais e pendências',
                texto: 'Quantidade de turmas e cursos e quantos polos ainda faltam no CRM.'
            },
            {
                alvo: '.csp .ec-form-acoes',
                titulo: 'Exportação com retorno claro',
                texto: 'Ao exportar, o resultado de cada oferta (exportada ou com falha) fica visível até você fechar.'
            }
        ]
    },
    {
        id: 'inscricao-exportar',
        titulo: 'Exportar oferta de inscrição web',
        pagina: new RegExp(UNIDADE + '/oferta_inscricao_web/create'),
        passos: [
            {
                alvo: '.eoi-passos',
                titulo: 'Passo a passo na tela',
                texto: 'Busque as turmas liberadas no ERP, confira período, modalidade e valor, e exporte para o CRM.'
            },
            {
                alvo: '.eoi-barra',
                titulo: 'Resumo e filtros',
                texto: 'Totais de turmas exportadas, com falha e sem modalidade. Busque por curso ou turma e filtre <b>Só inscrições abertas</b>.'
            },
            {
                alvo: '.eoi-lista',
                titulo: 'Turmas agrupadas por curso',
                texto: 'Cada turma mostra a situação das inscrições e o retorno da última exportação.'
            }
        ]
    },
    {
        id: 'inscricao-detalhe',
        titulo: 'Detalhes da inscrição web',
        presente: '.csw',
        passos: [
            {
                alvo: '.csw > .ec-card',
                titulo: 'Dados da liberação',
                texto: 'Curso, modalidade, nível de ensino e estrutura acadêmica vindos do ERP, num só card.'
            },
            {
                alvo: '.csw-filtros, .csw-lista',
                titulo: 'Turmas da oferta',
                texto: 'As turmas vinculadas aparecem em lista, com filtros quando houver muitas.'
            }
        ]
    },
    {
        id: 'config-unidade',
        titulo: 'Configurações da unidade',
        presente: '.cfg-abas',
        passos: [
            {
                alvo: '.cfg-abas',
                titulo: 'Abas que avisam',
                texto: 'Uma bolinha vermelha indica erro e uma amarela, algo pendente. Documentos mostra quantos já estão relacionados.'
            },
            {
                alvo: '.cfg-macros',
                titulo: 'Monte a máscara clicando',
                texto: 'Clique numa macro para adicioná-la à máscara escolhida. As já usadas ficam marcadas.'
            },
            {
                alvo: '.cfg-ajuda',
                titulo: 'Exemplos prontos',
                texto: 'Regras das máscaras e exemplos com a prévia do nome da oferta. Use um exemplo com um clique.'
            },
            {
                alvo: '.cfg-exportar',
                titulo: 'Salvar e exportar',
                texto: 'Alterações não salvas são sinalizadas, <kbd>Ctrl</kbd>+<kbd>S</kbd> salva e a exportação da unidade ou do local de oferta para o CRM fica aqui.'
            }
        ]
    },
    {
        id: 'cronjobs-lista',
        titulo: 'Gerenciador de CronJobs',
        presente: '.ec-tarefas',
        passos: [
            {
                alvo: '.ec-tarefas__situacoes',
                titulo: 'Filtre por situação',
                texto: 'Ativas, pausadas ou na lixeira: clique num cartão para ver só aquelas tarefas.'
            },
            {
                alvo: '.ec-tarefas .ec-listagem__toolbar',
                titulo: 'Busca e filtros',
                texto: 'Encontre tarefas pelo comando ou pela descrição.'
            },
            {
                alvo: '.ec-tarefas .ec-listagem__rolagem',
                titulo: 'Frequência em português',
                texto: 'A expressão cron vem descrita em português e a próxima execução aparece em tempo relativo (ex.: "daqui a 2 horas").'
            }
        ]
    },
    {
        id: 'cronjobs-formulario',
        titulo: 'Formulário de tarefa',
        presente: '.ec-tarefa-busca',
        passos: [
            {
                alvo: '.ec-tarefa-busca',
                titulo: 'Busque o comando',
                texto: 'Digite para filtrar os comandos do sistema. Ao escolher, a descrição e os parâmetros dele aparecem abaixo.'
            },
            {
                alvo: '.ec-tarefa-previa',
                titulo: 'Prévia do comando',
                texto: 'Veja exatamente a linha que será executada antes de salvar.'
            }
        ]
    },
    {
        id: 'cronjobs-historico',
        titulo: 'Histórico de execuções',
        presente: '.ec-historico',
        passos: [
            {
                alvo: '.ec-historico__resumo',
                titulo: 'Resumo da tarefa',
                texto: 'Frequência, próxima e última execução e total de execuções registradas, num relance.'
            },
            {
                alvo: '.ec-historico__lista',
                titulo: 'Execuções por dia',
                texto: 'As execuções ficam agrupadas por dia. Clique numa delas para ver a saída completa.'
            }
        ]
    }
]
